import React, { useEffect, useMemo, useState } from 'react';

import folder from '../../../../../assets/Imagess/folderICon.svg';

import { Formik, Form } from 'formik';
import * as Yup from 'yup';

import ModalChildrenContainer from '../../../../Atoms/Modal/ModalChildrenContainer';
import TextInput from '../../../../Atoms/Inputs/TextInput';
import LoaderButton from '../../../../Molecules/Loader/LoaderButton';
import Buttons from '../../../../Atoms/Buttons/Buttons';
import Modal from '../../../../Atoms/Modal/Modal';
import { useSaveGraduationStatistics } from '../../../../../Hooks/Statistics';
import { useToast } from '../../../../Molecules/Custom/CustomToaster/Toast';
import CustomSelect from '../../../../Atoms/forms/customSelect/CustomSelect';
import hashIcon from '../../../../../assets/icons/formActions/inputs/hash.svg';

const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

const toNum = (v) => {
  const n = Number(String(v ?? '').replace(/[^\d.-]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const asStr = (v) => (v === undefined || v === null ? '' : String(v));

const numSchema = Yup.number()
  .typeError('الرجاء إدخال رقم صحيح')
  .min(0, 'لا يمكن أن يكون الرقم سالباً')
  .required('هذا الحقل مطلوب');

const posSchema = Yup.number()
  .typeError('الرجاء إدخال رقم صحيح')
  .min(0, 'لا يمكن أن يكون الرقم سالباً')
  .max(100, 'القيمة يجب أن تكون بين 0 و 100')
  .required('هذا الحقل مطلوب');

const normalize = (v) =>
  String(v ?? '')
    .trim()
    .toLowerCase();

const COUNTRIES_API =
  'https://restcountries.com/v3.1/all?fields=cca2,name,translations';

const CreateCountryModal = ({
  mode = 'create',
  selectedItem,
  handleOnClose,
  refetch,
}) => {
  const toast = useToast();
  const { mutate: saveStat, isLoading } = useSaveGraduationStatistics();

  const s = selectedItem ?? {};

  const [remoteCountries, setRemoteCountries] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState('');

  useEffect(() => {
    const ac = new AbortController();

    const load = async () => {
      setCountriesLoading(true);
      setCountriesError('');

      try {
        const res = await fetch(COUNTRIES_API, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        const options = (Array.isArray(data) ? data : [])
          .map((c) => {
            const code = String(c?.cca2 ?? '')
              .trim()
              .toUpperCase();
            if (!code) return null;

            const ar =
              c?.translations?.ara?.common ||
              c?.translations?.ara?.official ||
              c?.name?.common ||
              code;

            const en = c?.name?.common || code;

            return {
              value: code,
              text_AR: String(ar ?? '').trim(),
              text_EN: String(en ?? '').trim(),
            };
          })
          .filter(Boolean)
          .sort((a, b) =>
            a.text_AR.localeCompare(b.text_AR, 'ar', { sensitivity: 'base' })
          );

        setRemoteCountries(options);
      } catch (e) {
        if (e?.name !== 'AbortError') {
          console.error(e);
          setCountriesError('فشل تحميل قائمة الدول');
        }
      } finally {
        setCountriesLoading(false);
      }
    };

    load();
    return () => ac.abort();
  }, []);

  const graduationStatisticsId = useMemo(() => {
    if (mode !== 'edit') return EMPTY_GUID;
    return s?.graduationStatisticsId || s?.id || s?.statisticsId || EMPTY_GUID;
  }, [mode, s]);

  const countryOptions = useMemo(() => {
    return (Array.isArray(remoteCountries) ? remoteCountries : [])
      .map((c) => ({
        value: String(c?.value ?? '').trim(),
        text: String(c?.text_AR ?? c?.text_EN ?? c?.value ?? '').trim(),
        raw: c,
      }))
      .filter((o) => o.value && o.text);
  }, [remoteCountries]);

  const initialValues = useMemo(() => {
    if (mode !== 'edit') {
      return {
        flagURL: '',
        countryName_AR: '',
        maleCount: '',
        femaleCount: '',
        bachelorCount: '',
        mastersCount: '',
        phdCount: '',
        positionX: '',
        positionY: '',
      };
    }

    return {
      flagURL: asStr(
        s?.flagURL ?? s?.flagUrl ?? s?.code ?? s?.countryCode ?? ''
      ),
      countryName_AR: asStr(
        s?.countryName_AR ?? s?.countryNameAr ?? s?.name_AR ?? ''
      ),
      maleCount: asStr(s?.maleCount ?? s?.male ?? ''),
      femaleCount: asStr(s?.femaleCount ?? s?.female ?? ''),
      bachelorCount: asStr(
        s?.bachelorCount ?? s?.bachelors ?? s?.bachelor ?? ''
      ),
      mastersCount: asStr(s?.mastersCount ?? s?.masters ?? ''),
      phdCount: asStr(s?.phdCount ?? s?.phd ?? ''),
      positionX: asStr(s?.positionX ?? ''),
      positionY: asStr(s?.positionY ?? ''),
    };
  }, [mode, s]);

  const validationSchema = Yup.object().shape({
    flagURL: Yup.string().required('الدولة مطلوبة'),
    maleCount: numSchema,
    femaleCount: numSchema,
    bachelorCount: numSchema,
    mastersCount: numSchema,
    phdCount: numSchema,
    positionX: posSchema,
    positionY: posSchema,
  });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={(values) => {
        const fd = new FormData();

        const append = (k, v) => {
          // FormData values are strings (or Blob/File)
          fd.append(k, v === undefined || v === null ? '' : String(v));
        };

        append('graduationStatisticsId', graduationStatisticsId);

        append('flagURL', values.flagURL);
        append('countryName_AR', values.countryName_AR);
        append('countryName_EN', values.countryName_AR);

        append('maleCount', toNum(values.maleCount));
        append('femaleCount', toNum(values.femaleCount));

        append('bachelorCount', toNum(values.bachelorCount));
        append('mastersCount', toNum(values.mastersCount));
        append('phdCount', toNum(values.phdCount));

        append('positionX', toNum(values.positionX));
        append('positionY', toNum(values.positionY));

        saveStat(fd, {
          onSuccess: () => {
            toast.open(
              mode === 'edit' ? 'تم التحديث بنجاح' : 'تمت الإضافة بنجاح'
            );
            refetch?.();
            handleOnClose?.();
          },
          onError: (e) => {
            console.error(e);
            const noResponse =
              !e?.response &&
              (e?.message?.includes('Network') || e?.name === 'TypeError');

            toast.open(
              noResponse
                ? 'تعذر الاتصال بالخادم، تأكد من الإنترنت وحاول مرة أخرى'
                : 'حدث خطأ أثناء الحفظ'
            );
          },
        });
      }}
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => {
        const total = toNum(values.maleCount) + toNum(values.femaleCount);

        const handleCountrySelect = (fieldName, code) => {
          setFieldValue(fieldName, code);

          const found = countryOptions.find(
            (o) => normalize(o.value) === normalize(code)
          );

          setFieldValue('countryName_AR', found?.text ?? '');
        };

        return (
          <Form>
            <Modal
              onClose={handleOnClose}
              title={mode === 'edit' ? 'تعديل دولة' : 'إضافة دولة جديدة'}
              width='40%'
              closeWhite={false}
            >
              <ModalChildrenContainer noOverflow>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1.2rem',
                    direction: 'rtl',
                  }}
                >
                  <CustomSelect
                    label='الدولة'
                    required
                    right
                    // icon={hashIcon}
                    placeholder={
                      countriesLoading ? 'جاري التحميل...' : 'اختر الدولة'
                    }
                    name='flagURL'
                    value={values.flagURL}
                    options={countryOptions}
                    onChange={handleCountrySelect}
                    errors={errors.flagURL || countriesError}
                    touched={touched.flagURL || Boolean(countriesError)}
                  />

                  {/* إجمالي الخريجين */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.2rem',
                    }}
                  >
                    <div style={{ color: '#666' }}>إجمالي الخريجين</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>
                      {total}
                    </div>
                  </div>

                  {/* ذكور / إناث */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    <TextInput
                      label='ذكور'
                      icon={hashIcon}
                      name='maleCount'
                      placeholder='ادخل الرقم'
                      value={values.maleCount}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.maleCount}
                      touched={touched.maleCount}
                      left
                    />

                    <TextInput
                      label='إناث'
                      icon={hashIcon}
                      name='femaleCount'
                      placeholder='ادخل الرقم'
                      value={values.femaleCount}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.femaleCount}
                      touched={touched.femaleCount}
                      left
                    />
                  </div>

                  {/* الشهادات */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    <TextInput
                      label='بكالوريوس'
                      icon={hashIcon}
                      name='bachelorCount'
                      placeholder='ادخل الرقم'
                      value={values.bachelorCount}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.bachelorCount}
                      touched={touched.bachelorCount}
                      left
                    />

                    <TextInput
                      label='ماجستير'
                      icon={hashIcon}
                      name='mastersCount'
                      placeholder='ادخل الرقم'
                      value={values.mastersCount}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.mastersCount}
                      touched={touched.mastersCount}
                      left
                    />
                  </div>

                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    <TextInput
                      label='دكتوراه'
                      icon={hashIcon}
                      name='phdCount'
                      placeholder='ادخل الرقم'
                      value={values.phdCount}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.phdCount}
                      touched={touched.phdCount}
                      left
                    />
                    <div />
                  </div>

                  {/* الموقع X/Y */}
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '1rem',
                    }}
                  >
                    <TextInput
                      label='الموقع X (0 - 100)'
                      icon={hashIcon}
                      name='positionX'
                      placeholder='ادخل الرقم'
                      value={values.positionX}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.positionX}
                      touched={touched.positionX}
                      left
                    />

                    <TextInput
                      label='الموقع Y (0 - 100)'
                      icon={hashIcon}
                      name='positionY'
                      placeholder='ادخل الرقم'
                      value={values.positionY}
                      required
                      onChange={(name, value) => setFieldValue(name, value)}
                      errors={errors.positionY}
                      touched={touched.positionY}
                      left
                    />
                  </div>

                  {/* keep AR name in form state but don't show it */}
                  <div style={{ display: 'none' }}>
                    <input
                      name='countryName_AR'
                      value={values.countryName_AR}
                      readOnly
                    />
                  </div>

                  {/* Actions */}
                  {isLoading ? (
                    <LoaderButton />
                  ) : (
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '1rem',
                        marginTop: '0.6rem',
                      }}
                    >
                      <button
                        type='button'
                        onClick={handleOnClose}
                        style={{
                          height: '46px',
                          borderRadius: '999px',
                          border: '0',
                          background: '#bdbdbd',
                          color: '#fff',
                          fontWeight: 700,
                          cursor: 'pointer',
                        }}
                      >
                        إلغاء
                      </button>

                      <Buttons
                        text={mode === 'edit' ? 'تحديث' : 'حفظ'}
                        handleOnClick={handleSubmit}
                        rounded
                      />
                    </div>
                  )}
                </div>
              </ModalChildrenContainer>
            </Modal>
          </Form>
        );
      }}
    </Formik>
  );
};

export default CreateCountryModal;
